import { CloseRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { moduleTypes } from "../constants/landing-page-constants";
import { black, greyEnabled, purple, purpleLight } from "../../../theme/colors";

export interface LandingPageFilterValues {
  module: string | null;
  fromDate: string;
  toDate: string;
}

interface FilterLandingPageProps extends LandingPageFilterValues {
  open: boolean;
  onClose: () => void;
  onApply: (filters: LandingPageFilterValues) => void;
}

const popoverPaperSx = {
  elevation: 0,
  sx: {
    mt: 0.5,
    borderRadius: 2,
    border: "1px solid #ECECEC",
    boxShadow: "0px 12px 28px -8px rgba(27, 0, 27, 0.18)",
  },
} as const;

export default function FilterLandingPage({
  open,
  onClose,
  module,
  fromDate,
  toDate,
  onApply,
}: FilterLandingPageProps) {
  const [_module, _setModule] = useState<string | null>(module);
  const [_fromDate, _setFromDate] = useState<string>(fromDate);
  const [_toDate, _setToDate] = useState<string>(toDate);

  useEffect(() => {
    if (open) {
      _setModule(module);
      _setFromDate(fromDate);
      _setToDate(toDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Ensure end date is not before start date
    if (_toDate && _fromDate && dayjs(_fromDate, "YYYY-MM-DD").isAfter(dayjs(_toDate, "YYYY-MM-DD"))) {
      _setToDate("");
    }
  }, [_fromDate, _toDate]);

  const handleClose = () => onClose();

  const handleReset = () => {
    onApply({ module: null, fromDate: "", toDate: "" });
    onClose();
  };

  const handleApply = () => {
    onApply({ module: _module, fromDate: _fromDate, toDate: _toDate });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Content Pages
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Module
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {moduleTypes.map((option) => (
              <Chip
                key={option}
                label={option}
                clickable
                variant={_module === option ? "filled" : "outlined"}
                color="primary"
                onClick={() => _setModule((prev) => (prev === option ? null : option))}
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color={black} mt={2} mb={1}>
            Created Date
          </Typography>
          <Box display="flex" gap={1.5}>
            <DatePicker
              label="From Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_fromDate ? dayjs(_fromDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setFromDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              format="DD-MM-YYYY"
              disableFuture
              minDate={_fromDate ? dayjs(_fromDate, "YYYY-MM-DD") : undefined}
              value={_toDate ? dayjs(_toDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setToDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: popoverPaperSx, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
